import { useState } from "react"
import { addDays, differenceInCalendarDays, format, isSameDay, parseISO } from "date-fns"

// Weeks start on Monday, as they did in the original app.
export const WEEK_OPTS = { weekStartsOn: 1 }

// Properties can carry their own colour (Settings → Properties). Until they
// do, fall back to the original app's palette by slug.
const FALLBACK_COLORS = { gardens: "#4d7c0f", waters: "#0369a1" }
const NEUTRAL_COLOR = "#78716c"

export function propertyColor(property) {
  return property?.color || FALLBACK_COLORS[property?.slug] || NEUTRAL_COLOR
}

// "Jacky Winter Gardens" → "Gardens"
export function shortName(property) {
  return (property?.name || "").replace("Jacky Winter ", "")
}

// `kind` arrives with a later schema change; a booking without one is a stay.
export function isBlock(booking) {
  return booking.kind === "block"
}

export function isResidency(booking) {
  return booking.kind === "residency"
}

// Cancelled bookings stay in the API for history but never on the calendar.
export function isLive(booking) {
  return booking.status === "confirmed"
}

// Checked out already (ends_on is the checkout morning, so a stay ending today is past).
export function isPast(booking, today = new Date()) {
  const todayIso = today.toLocaleDateString("en-CA")
  return booking.ends_on <= todayIso
}

// Airbnb and Riparide import each other's calendars, so one hold often comes
// back from both feeds, and a hold Jeremy set on both platforms arrives twice.
// Airbnb also merges neighbouring holds into one long one (the hold for a stay
// ending on the 19th plus a hold for the next one on the 19th comes back as a
// single block). Within a property, drop any block whose nights are all already
// covered by live stays, or that sits inside another block. Past blocks are
// dropped outright -- there's nothing to do about them any more.
function nightsOf(booking) {
  const nights = []
  for (let day = parseISO(booking.starts_on); day < parseISO(booking.ends_on); day = addDays(day, 1)) {
    nights.push(format(day, "yyyy-MM-dd"))
  }
  return nights
}

export function calendarBookings(bookings, today = new Date()) {
  const live = bookings.filter(isLive)
  const stays = live.filter((b) => !isBlock(b))
  const keep = [ ...stays ]

  const coveredNights = new Map() // property_id -> Set of ISO nights taken by a stay
  stays.forEach((stay) => {
    if (!coveredNights.has(stay.property_id)) coveredNights.set(stay.property_id, new Set())
    nightsOf(stay).forEach((night) => coveredNights.get(stay.property_id).add(night))
  })
  const coveredByStays = (block) => {
    const taken = coveredNights.get(block.property_id)
    return !!taken && nightsOf(block).every((night) => taken.has(night))
  }
  const sameProperty = (a, b) => a.property_id === b.property_id
  const inside = (outer, inner) => sameProperty(outer, inner) && outer.starts_on <= inner.starts_on && outer.ends_on >= inner.ends_on

  live
    .filter((b) => isBlock(b) && !isPast(b, today) && !coveredByStays(b))
    .sort((a, b) => a.starts_on.localeCompare(b.starts_on) || b.ends_on.localeCompare(a.ends_on))
    .forEach((block) => {
      if (!keep.some((other) => other.id !== block.id && isBlock(other) && inside(other, block))) keep.push(block)
    })
  return keep
}

// The API embeds each booking's clean; flatten those into the standalone list
// the grid and agenda work from. A changeover clean is pinned to the first day of
// its window. Declined cleans are dropped -- they're no longer anyone's job.
export function cleansFrom(bookings) {
  return bookings
    .filter((b) => b.clean && b.clean.status !== "declined")
    .map((b) => ({
      ...b.clean,
      booking_id: b.id,
      property_id: b.property_id,
      scheduled_on: b.clean.window_starts_on,
    }))
}

// Stacks segments (each with a 0-based `col` and `span`) into rows greedily so
// overlapping ones sit on separate rows. Returns the number of rows used.
export function packRows(segments) {
  const rows = [] // rows[i] = first free column in that row
  segments
    .sort((a, b) => a.col - b.col || b.span - a.span)
    .forEach((seg) => {
      let row = rows.findIndex((free) => free <= seg.col)
      if (row === -1) row = rows.push(0) - 1
      rows[row] = seg.col + seg.span
      seg.row = row
    })
  return rows.length
}

// Each day is two half-day columns (14 per week): a stay begins on the
// afternoon of check-in day and ends on the morning of check-out day, so the
// bar visibly covers the checkout date and a back-to-back changeover shows both
// stays meeting on one day, on the same row. Segments are clipped to the week.
export const HALF_COLS = 14

export function weekSegments(bookings, weekStart) {
  const weekEnd = addDays(weekStart, 6)
  const segments = bookings
    .map((booking) => {
      const start = parseISO(booking.starts_on)
      const end = parseISO(booking.ends_on)
      if (end < weekStart || start > weekEnd) return null

      const startsHere = start >= weekStart
      const endsHere = end <= weekEnd
      const col = startsHere ? differenceInCalendarDays(start, weekStart) * 2 + 1 : 0
      const colEnd = endsHere ? differenceInCalendarDays(end, weekStart) * 2 + 1 : HALF_COLS
      const span = Math.max(1, colEnd - col)

      return { booking, col, span, startsHere, endsHere, row: 0 }
    })
    .filter(Boolean)

  packRows(segments)
  return segments
}

// A clean covers whole days: from the morning of its first day to the
// afternoon of its last (two days for a changeover).
export function cleanSegments(cleans, weekStart) {
  const weekEnd = addDays(weekStart, 6)
  const segments = cleans
    .map((clean) => {
      const start = parseISO(clean.window_starts_on)
      const end = parseISO(clean.window_ends_on || clean.window_starts_on)
      if (end < weekStart || start > weekEnd) return null

      const first = start < weekStart ? weekStart : start
      const last = end > weekEnd ? weekEnd : end
      const col = differenceInCalendarDays(first, weekStart) * 2
      const span = (differenceInCalendarDays(last, first) + 1) * 2
      return { clean, col, span, startsHere: start >= weekStart, endsHere: end <= weekEnd, row: 0 }
    })
    .filter(Boolean)

  packRows(segments)
  return segments
}

export const CLEAN_STATUS_STYLES = {
  pending: "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  accepted: "border-green-300 bg-green-100 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200",
  done: "border-border bg-muted text-muted-foreground",
  flagged: "border-red-300 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200",
}

export function cleanStyle(clean) {
  if (clean.flagged_reason) return CLEAN_STATUS_STYLES.flagged
  return CLEAN_STATUS_STYLES[clean.status] || CLEAN_STATUS_STYLES.done
}

export function cleanerFirstName(clean) {
  return (clean.cleaner_name || "Unassigned").split(" ")[0]
}

export const SOURCE_BADGES = {
  airbnb: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  riparide: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  direct: "bg-muted text-muted-foreground",
}

// useState backed by localStorage. Reads and writes are wrapped so a private
// window or blocked storage just falls back to plain in-memory state.
export function usePersistedState(key, initial) {
  const [ value, setValue ] = useState(() => {
    try {
      const stored = localStorage.getItem(key)
      if (stored === null) return initial
      const parsed = JSON.parse(stored)
      return typeof initial === "object" && initial !== null ? { ...initial, ...parsed } : parsed
    } catch {
      return initial
    }
  })

  const update = (next) => {
    setValue(next)
    try {
      localStorage.setItem(key, JSON.stringify(next))
    } catch {
      // storage unavailable -- state still updates for this session
    }
  }

  return [ value, update ]
}
